import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { BlogPost, BlogPostSummary } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowLeft, ArrowUpRight } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { getPostBySlug, getRelatedPosts } from '@/lib/blog'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Cta } from '@/components/generals/cta'
import { PostMeta } from '@/components/generals/post-meta'
import { SectionLabel } from '@/components/generals/section-label'
import RichText from '@/components/generals/rich-text'
import { LINKS } from '@/constants/site'

interface PostPageProps {
    post: BlogPost
    related: BlogPostSummary[]
}

const PostPage: FC<PostPageProps> = ({ post, related }): ReactNode => {
    const article = useRef<HTMLElement>(null)

    useScrollProgress(article, { startAt: 0.2, endAt: 0.9 })

    return (
        <Layout header='light'>
            <Seo
                title={post.title}
                description={post.excerpt ?? undefined}
                ogImage={post.coverImage ?? undefined}
                type='article'
            />

            <article ref={article} className='tone-paper relative' data-header='light'>
                <div className='article-progress fixed inset-x-0 top-0 z-40 h-[3px] bg-signal' aria-hidden='true' />

                <header className='wrap pt-[calc(var(--header-h)+4rem)]'>
                    <Link href='/blogg' className='load-fade t-label inline-flex items-center gap-2 text-tone-muted hover:text-tone-fg' style={{ '--load-delay': '0.05s' } as CSSProperties}>
                        <ArrowLeft className='size-4' aria-hidden='true' />
                        Bloggen
                    </Link>

                    <div className='mt-10 grid grid-cols-12 gap-x-6 gap-y-8'>
                        <div className='col-span-12 lg:col-span-9'>
                            <PostMeta post={post} className='load-fade' />
                            <h1 className='load-fade t-h1 mt-6 balance' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                                <span data-mdlfy-cms={`blog_posts:${post.id}:title`}>{post.title}</span>
                            </h1>
                        </div>
                    </div>
                </header>

                {post.coverImage && (
                    <figure className='wrap mt-12 md:mt-16'>
                        <div className='aspect-[16/9] overflow-hidden bg-tone-line' data-reveal='frame'>
                            <img
                                src={post.coverImage}
                                alt={post.coverImageAlt ?? ''}
                                fetchPriority='high'
                                decoding='async'
                                className='size-full object-cover'
                            />
                        </div>
                    </figure>
                )}

                <div className='wrap grid grid-cols-12 gap-x-6 gap-y-12 py-16 md:py-24'>
                    <aside className='col-span-12 lg:col-span-3'>
                        <div className='lg:sticky lg:top-28'>
                            <dl className='t-label grid grid-cols-2 gap-4 text-tone-muted lg:grid-cols-1'>
                                <div>
                                    <dt>Publisert</dt>
                                    <dd className='mt-1 text-tone-fg'>{formatDate(post.publishedAt)}</dd>
                                </div>
                                {post.readTime && (
                                    <div>
                                        <dt>Lesetid</dt>
                                        <dd className='mt-1 text-tone-fg'>
                                            <span data-mdlfy-cms={`blog_posts:${post.id}:read_time`}>{post.readTime}</span> min
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    </aside>

                    <div className='col-span-12 lg:col-span-7'>
                        {post.excerpt && (
                            <p className='t-lead pretty' data-mdlfy-cms={`blog_posts:${post.id}:excerpt`}>{post.excerpt}</p>
                        )}

                        {post.body
                            ? <RichText html={post.body} className='mt-10 t-body' />
                            : post.originalUrl && (
                                <div className='mt-12 border-t border-tone-fg pt-8'>
                                    <p className='t-h3 balance'>Hele artikkelen ligger på svenn.com.</p>
                                    <a href={post.originalUrl} target='_blank' rel='noopener noreferrer' className='btn btn-lg mt-6'>
                                        <span>Les hele artikkelen</span>
                                        <ArrowUpRight className='size-4' aria-hidden='true' />
                                        <span className='sr-only'> (åpnes i ny fane)</span>
                                    </a>
                                </div>
                            )}

                        <div className='mt-16 bg-night p-6 text-paper md:p-10'>
                            <p className='t-label text-mist'>Prøv Svenn</p>
                            <p className='t-h2 mt-4 balance'>Bygg overskudd fra første spiker.</p>
                            <p className='mt-4 max-w-md text-mist'>14 dager helt gratis. Krever ikke kredittkort, og ingen auto-abonnement.</p>
                            <div className='tone-night mt-8'>
                                <Cta href={LINKS.signup}>Prøv gratis i 14 dager</Cta>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            {related.length > 0 && (
                <section id='les-mer' className='tone-mist relative py-20 md:py-28' data-header='light'>
                    <div className='wrap'>
                        <SectionLabel>Les mer</SectionLabel>
                        <ul className='mt-10 grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-3'>
                            {related.map((item, i) => (
                                <li key={item.id} className='group' data-reveal='rise' style={{ '--d': i } as CSSProperties}>
                                    <Link href={`/blogg/${item.slug}`} className='block' tabIndex={-1} aria-hidden='true'>
                                        <div className='aspect-[4/3] overflow-hidden bg-tone-line'>
                                            {item.coverImage && (
                                                <img src={item.coverImage} alt='' loading='lazy' decoding='async' className='size-full object-cover transition-transform duration-[1.2s] ease-out-quint group-hover:scale-105' />
                                            )}
                                        </div>
                                    </Link>
                                    <PostMeta post={item} showDate={false} className='mt-5' />
                                    <h2 className='mt-3 text-xl font-semibold leading-snug tracking-tight balance'>
                                        <Link href={`/blogg/${item.slug}`} className='u-link'>
                                            <span data-mdlfy-cms={`blog_posts:${item.id}:title`}>{item.title}</span>
                                        </Link>
                                    </h2>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: [],
    fallback: 'blocking'
})

export const getStaticProps: GetStaticProps<PostPageProps> = async ({ params }) => {
    const slug = String(params?.slug ?? '')
    const post = await getPostBySlug(slug)

    if (!post) return { notFound: true, revalidate: 60 }

    let related: BlogPostSummary[] = []

    try {
        related = await getRelatedPosts(post, 3)
    }
    catch (error) {
        console.error('Could not load related posts', error instanceof Error ? error.message : error)
    }

    return { props: { post, related }, revalidate: 300 }
}

export default PostPage
